import {
  MutableRefObject,
  useEffect,
  useLayoutEffect,
  useRef,
} from 'react';
import {
  MessageDisplay,
  selectCurrentSelectedChat,
  selectMetadataById,
} from 'models/twilio';
import { useSelector } from 'react-redux';
import last from 'lodash/last';
import useCurrentAttendee from './useCurrentAttendee';
import generateMessageId from '../utils/generateMessageId';
import renderUnreadIndicator from '../utils/renderUnreadIndicator';

const useChatSideEffect = (
  messageListRef: MutableRefObject<HTMLDivElement>,
  initialMessageFetch: () => Promise<void>,
  displayGroup: MessageDisplay,
  setAllMessagesConsumed: () => Promise<void>,
) => {
  const { isCurrentAttendee } = useCurrentAttendee();
  const lastMessageIndexRef = useRef<number>(null);
  const currentSelectedChat = useSelector(selectCurrentSelectedChat);
  const metadata = useSelector(selectMetadataById(currentSelectedChat));

  useEffect(() => {
    const init = async () => {
      if (!metadata?.id) return;
      if (!metadata.hasLoadedMessages) {
        await initialMessageFetch();
      }
    };
    init();
  }, [metadata?.id, metadata?.hasLoadedMessages, initialMessageFetch]);

  // When the user opens the chat, scroll the last read message into view.
  // Execute once after messages load
  // Renders the unread indicator if required
  useLayoutEffect(() => {
    if (metadata?.hasLoadedMessages !== true) return;
    const { lastReadMessageIndex, lastMessage } = metadata;
    const unreadMessage = document.getElementById(generateMessageId(lastReadMessageIndex + 1));
    if (unreadMessage && lastMessage && lastMessage.index > lastReadMessageIndex) {
      unreadMessage.scrollIntoView({ block: 'center' });
      renderUnreadIndicator(unreadMessage, metadata);
    }
    if (!unreadMessage && messageListRef.current) {
      const $chat = messageListRef.current;
      $chat.scrollTop = $chat.scrollHeight - $chat.clientHeight;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [metadata?.id, metadata?.hasLoadedMessages]);

  // Consume all messages if chat window is not scrollable
  useLayoutEffect(() => {
    const messageList = messageListRef.current;
    if (metadata?.hasLoadedMessages !== true || !messageList) return;
    const isMessageListOverflow = messageList.scrollHeight > messageList.clientHeight;
    if (isMessageListOverflow) return;
    setAllMessagesConsumed();
  }, [metadata?.id, metadata?.hasLoadedMessages, messageListRef, displayGroup, setAllMessagesConsumed]);

  // Scroll down when new message sent by current attendee
  useEffect(() => {
    const $chat = messageListRef.current;
    if (!$chat) return;
    if (displayGroup.length) {
      const { messageGroups } = last(displayGroup);
      const { messages } = last(messageGroups);
      const lastMessage = last(messages);
      if (isCurrentAttendee(lastMessage.author) && lastMessageIndexRef.current !== lastMessage.index) {
        $chat.scroll({ behavior: 'smooth', top: $chat.scrollHeight });
      }
      lastMessageIndexRef.current = lastMessage.index;
    }
  }, [isCurrentAttendee, messageListRef, displayGroup]);
};

export default useChatSideEffect;
